import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../../services/api.service';
import './Transformations.css';

const APIIntegration = ({ setActiveTab }) => {
  const [selectedEndpoint, setSelectedEndpoint] = useState('titanPassthrough');
  const [requestFormat, setRequestFormat] = useState('json'); // 'json' or 'xml'
  const [requestBody, setRequestBody] = useState('');
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configs, setConfigs] = useState({});
  const [organization, setOrganization] = useState(null);

  const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.REACT_APP_API_URL || 'http://localhost:3003/api/v1';

  // Fetch configurations and organization on mount
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const data = await apiService.get('/config');
        if (data.success) {
          const configMap = {};
          data.data.forEach(item => {
            configMap[item.key] = item.value;
          });
          setConfigs(configMap);
        }
      } catch (err) {
        console.error('Failed to fetch configs:', err);
      }
    };

    const fetchOrganization = async () => {
      try {
        const data = await apiService.get('/organizations/me');
        if (data.success && data.data) {
          console.log('Organization loaded:', data.data);
          setOrganization(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch organization:', err);
      }
    };

    fetchConfigs();
    fetchOrganization();
  }, []);

  // Get the orgName for the Titan passthrough URL
  const orgName = organization?.titan_name || 'your-org-name';

  // Memoize endpoints so they update when organization changes
  const endpoints = useMemo(() => ({
    titanPassthrough: {
      name: 'Serial Number Request',
      method: 'POST',
      url: `${API_BASE_URL}/serials/request/${orgName}`,
      description: `Proxies raw XML to rfXcel using credentials stored for your organization (${organization?.name || 'loading...'}).`,
      exampleRequestJson: {
        note: "This endpoint expects raw XML in the body. If sending JSON, use the 'sampleData' property.",
        sampleData: `<ss:syncAllocateTraceIds xmlns:ss="http://xmlns.rfxcel.com/traceability/serializationService/3"
    contentStructVer="3.1.3"
    createDateTime="${new Date().toISOString()}"
    requestId="REQ-${Date.now()}">
  <ss:orgId qlfr="GS1_COMPANY_PREFIX">ORG123456</ss:orgId>
  <ss:allocOrgId qlfr="GS1_COMPANY_PREFIX">ORG123456</ss:allocOrgId>
  <ss:eventId>EVT-${Date.now()}</ss:eventId>
  <ss:itemId qlfr="GTIN">00380388820006</ss:itemId>
  <ss:siteHierId qlfr="SITE_HIERARCHY">Manufacturing</ss:siteHierId>
  <ss:siteId type="GLN" qlfr="GLN">0380388000001</ss:siteId>
  <ss:idTextFormat>TAG_URI</ss:idTextFormat>
  <ss:separatePrefixSuffix>true</ss:separatePrefixSuffix>
  <ss:returnDataStruct>LIST</ss:returnDataStruct>
  <ss:idCount>1</ss:idCount>
</ss:syncAllocateTraceIds>`
      },
      exampleRequestXml: `<ss:syncAllocateTraceIds xmlns:ss="http://xmlns.rfxcel.com/traceability/serializationService/3"
    contentStructVer="3.1.3"
    createDateTime="${new Date().toISOString()}"
    requestId="REQ-${Date.now()}">
  <ss:orgId qlfr="GS1_COMPANY_PREFIX">ORG123456</ss:orgId>
  <ss:allocOrgId qlfr="GS1_COMPANY_PREFIX">ORG123456</ss:allocOrgId>
  <ss:eventId>EVT-${Date.now()}</ss:eventId>
  <ss:itemId qlfr="GTIN">00380388820006</ss:itemId>
  <ss:siteHierId qlfr="SITE_HIERARCHY">Manufacturing</ss:siteHierId>
  <ss:siteId type="GLN" qlfr="GLN">0380388000001</ss:siteId>
  <ss:idTextFormat>TAG_URI</ss:idTextFormat>
  <ss:separatePrefixSuffix>true</ss:separatePrefixSuffix>
  <ss:returnDataStruct>LIST</ss:returnDataStruct>
  <ss:idCount>1</ss:idCount>
</ss:syncAllocateTraceIds>`
    }
  }), [API_BASE_URL, orgName, organization?.name]);

  console.log('Endpoints:', Object.keys(endpoints));

  React.useEffect(() => {
    setActiveTab('api-integration');
    // Initialize with example request based on format
    const endpoint = endpoints[selectedEndpoint];
    if (requestFormat === 'json') {
      setRequestBody(JSON.stringify(endpoint.exampleRequestJson, null, 2));
    } else {
      setRequestBody(endpoint.exampleRequestXml);
    }
  }, [setActiveTab, selectedEndpoint, requestFormat]);

  const handleTestAPI = async () => {
    setIsLoading(true);
    setError(null);
    setResponse(null);

    try {
      const endpoint = endpoints[selectedEndpoint];

      let headers = {};
      let body;

      if (requestFormat === 'xml') {
        // Validate XML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(requestBody, "text/xml");
        const parseError = xmlDoc.getElementsByTagName("parsererror");
        if (parseError.length > 0) {
          throw new Error(`Invalid XML: ${parseError[0].textContent}`);
        }

        // XML request - send with XML headers
        headers = {
          'Content-Type': 'text/xml',
          'X-RfXcel-Endpoint': 'https://stil-qa.track-n-trace.net/rfxcelwss/services/ISerializationServiceSoapHttpPort',
          'X-RfXcel-Username': 'stil_qa_cmo',
          'X-RfXcel-Password': 'ASSTIqa3645t_',
          'X-RfXcel-Operation': 'syncAllocateTraceIds'
        };
        body = requestBody;
      } else {
        // Validate JSON
        try {
          JSON.parse(requestBody);
        } catch (e) {
          throw new Error(`Invalid JSON: ${e.message}`);
        }

        // JSON request
        headers = {
          'Content-Type': 'application/json',
        };
        const parsedBody = JSON.parse(requestBody);
        body = JSON.stringify(parsedBody);
      }

      const res = await fetch(endpoint.url, {
        method: endpoint.method,
        headers,
        body
      });

      // Handle both JSON and XML responses
      const contentType = res.headers.get('Content-Type');
      let data;

      if (contentType && contentType.includes('xml')) {
        data = await res.text();
      } else {
        data = await res.json();
      }

      setResponse({
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
        body: data
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const currentEndpoint = endpoints[selectedEndpoint];

  return (
    <>
      {/* Top Header */}
      <header className="top-header">
        <div className="breadcrumb">
          <span>Relay</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">API Proxy</span>
        </div>
      </header>


      {/* API Integration Content */}
      <div className="tab-content active">
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600', color: '#111827' }}>
              API Integration & Testing
            </h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
              Test transformation APIs directly from your browser. Copy the endpoint URL and request examples for your integration.
            </p>
          </div>

          {/* Endpoint Selection */}
          <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
              Select API Endpoint
            </h3>
            <select
              value={selectedEndpoint}
              onChange={(e) => setSelectedEndpoint(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', backgroundColor: 'white' }}
            >
              {Object.entries(endpoints).map(([key, endpoint]) => (
                <option key={key} value={key}>{endpoint.name}</option>
              ))}
            </select>
          </div>

          {/* Endpoint Details */}
          <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ padding: '4px 12px', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
                {currentEndpoint.method}
              </span>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                {currentEndpoint.name}
              </h3>
            </div>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              {currentEndpoint.description}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <code style={{ flex: 1, padding: '12px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', fontFamily: 'monospace', fontSize: '13px', color: '#374151' }}>
                {currentEndpoint.url}
              </code>
              <button
                onClick={() => copyToClipboard(currentEndpoint.url)}
                style={{ padding: '10px 20px', backgroundColor: '#6366f1', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Copy URL
              </button>
            </div>
          </div>

          {/* Request/Response Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* Request Panel */}
            <div style={{ padding: '20px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                  Request Body
                </h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {/* Format Toggle */}
                  <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
                    <button
                      onClick={() => setRequestFormat('json')}
                      style={{
                        padding: '6px 16px',
                        backgroundColor: requestFormat === 'json' ? '#6366f1' : '#f9fafb',
                        color: requestFormat === 'json' ? 'white' : '#374151',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      JSON
                    </button>
                    <button
                      onClick={() => setRequestFormat('xml')}
                      style={{
                        padding: '6px 16px',
                        backgroundColor: requestFormat === 'xml' ? '#6366f1' : '#f9fafb',
                        color: requestFormat === 'xml' ? 'white' : '#374151',
                        border: 'none',
                        borderLeft: '1px solid #d1d5db',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      XML
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      if (requestFormat === 'json') {
                        setRequestBody(JSON.stringify(currentEndpoint.exampleRequestJson, null, 2));
                      } else {
                        setRequestBody(currentEndpoint.exampleRequestXml);
                      }
                    }}
                    style={{ padding: '6px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Reset to Example
                  </button>
                </div>
              </div>
              <textarea
                value={requestBody}
                onChange={(e) => setRequestBody(e.target.value)}
                style={{ width: '100%', minHeight: '400px', padding: '12px', fontFamily: 'monospace', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '6px', resize: 'vertical' }}
                placeholder={requestFormat === 'json' ? 'Enter JSON request body...' : 'Enter XML request body...'}
              />
              <button
                onClick={handleTestAPI}
                disabled={isLoading}
                style={{ marginTop: '16px', width: '100%', padding: '12px', backgroundColor: isLoading ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: isLoading ? 'not-allowed' : 'pointer' }}
              >
                {isLoading ? 'Testing...' : 'Test API'}
              </button>
            </div>

            {/* Response Panel */}
            <div style={{ padding: '20px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                  Response
                </h3>
                {response && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{ padding: '4px 12px', backgroundColor: response.status === 200 ? '#d1fae5' : '#fee2e2', color: response.status === 200 ? '#065f46' : '#991b1b', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
                      {response.status} {response.statusText}
                    </span>
                    <button
                      onClick={() => copyToClipboard(typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2))}
                      style={{ padding: '4px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
              {!response && !error && (
                <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>
                  Response will appear here after testing
                </div>
              )}
              {error && (
                <div style={{ padding: '16px', backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '13px' }}>
                  <strong>Error:</strong> {error}
                </div>
              )}
              {response && (
                <pre style={{ margin: 0, padding: '12px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', fontFamily: 'monospace', fontSize: '12px', overflow: 'auto', maxHeight: '400px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '100%' }}>
                  {typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2)}
                </pre>
              )}
            </div>
          </div>

          {/* cURL Example */}
          <div style={{ marginTop: '24px', padding: '20px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                cURL Example ({requestFormat.toUpperCase()})
              </h3>
              <button
                onClick={() => {
                  if (requestFormat === 'json') {
                    copyToClipboard(`curl -X ${currentEndpoint.method} ${currentEndpoint.url} \\\n-H "Content-Type: application/json" \\\n-d '${JSON.stringify(currentEndpoint.exampleRequestJson)}'`);
                  } else {
                    copyToClipboard(`curl -X ${currentEndpoint.method} ${currentEndpoint.url} \\\n-H "Content-Type: text/xml" \\\n-H "X-RfXcel-Endpoint: https://stil-qa.track-n-trace.net/rfxcelwss/services/ISerializationServiceSoapHttpPort" \\\n-H "X-RfXcel-Username: stil_qa_cmo" \\\n-H "X-RfXcel-Password: ASSTIqa3645t_" \\\n-H "X-RfXcel-Operation: syncAllocateTraceIds" \\\n-d '${currentEndpoint.exampleRequestXml.replace(/'/g, "'\\''")}'`);
                  }
                }}
                style={{ padding: '6px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
              >
                Copy cURL
              </button>
            </div>
            <pre style={{ margin: 0, padding: '12px', backgroundColor: '#1f2937', color: '#f9fafb', borderRadius: '6px', fontFamily: 'monospace', fontSize: '12px', overflow: 'auto' }}>
              {requestFormat === 'json'
                ? `curl -X ${currentEndpoint.method} ${currentEndpoint.url} \\
-H "Content-Type: application/json" \\
-d '${JSON.stringify(currentEndpoint.exampleRequestJson, null, 2)}'`
                : `curl -X ${currentEndpoint.method} ${currentEndpoint.url} \\
-H "Content-Type: text/xml" \\
-H "X-RfXcel-Endpoint: https://stil-qa.track-n-trace.net/rfxcelwss/services/ISerializationServiceSoapHttpPort" \\
-H "X-RfXcel-Username: stil_qa_cmo" \\
-H "X-RfXcel-Password: ASSTIqa3645t_" \\
-H "X-RfXcel-Operation: syncAllocateTraceIds" \\
-d '${currentEndpoint.exampleRequestXml}'`}
            </pre>
          </div>
        </div>
      </div>
    </>
  );
};

export default APIIntegration;
